import React, { useState, useEffect, useCallback } from 'react';
import { getApiBase } from './config/apiBase';
import { authService } from './services/authService';
import QaDocForm from './QaDocForm';
import CreateEpicPanel from './CreateEpicPanel';
import CreateStoriesPanel from './CreateStoriesPanel';
import MockupBuilder from './MockupBuilder';
import LyricConfirm from './components/LyricConfirm';
import JiraTicketLink from './components/JiraTicketLink';

const apiUrl = getApiBase();

const authenticatedFetch = async (url, options = {}) => {
  const token = await authService.getAccessToken();
  return fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
};

const STATUS_LABEL = {
  not_started: 'Not started',
  in_progress: 'In progress',
  complete: 'Complete',
};

// Derive a deliverable's status from the flow for a given definition step.
function stepStatus(flow, step) {
  if (!flow) return 'not_started';
  if (step.kind === 'jira') {
    const linked = flow.linkedTickets?.[step.jiraBucket] || [];
    return linked.length > 0 ? 'complete' : 'not_started';
  }
  return flow.deliverables?.[step.docType]?.status || 'not_started';
}

// A step is "unlocked" when every step it requires is complete. Gating is
// advisory — locked steps can still be opened, they just show a warning.
function isUnlocked(flow, step, stepsByKey) {
  if (!step.requires || step.requires.length === 0) return true;
  return step.requires.every((key) => {
    const dep = stepsByKey[key];
    return dep && stepStatus(flow, dep) === 'complete';
  });
}

/**
 * Product Discovery space picker for Step 1 (JPD Idea). Lists only Jira
 * projects of type `product_discovery` (Discovery spaces such as "PM - Product
 * Management"), kept deliberately separate from the software project chosen at
 * the top of the page. The chosen space scopes the Idea search below and is
 * persisted on the flow (jpdProjectKey) so it survives resume.
 */
function JpdSpacePicker({ value, onChange, disabled }) {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/projects`);
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.success) {
          const discovery = (data.projects || [])
            .filter((p) => p.projectTypeKey === 'product_discovery')
            .map((p) => ({ key: p.key, name: p.name }))
            .sort((a, b) => a.name.localeCompare(b.name));
          setSpaces(discovery);
        } else if (data.requiresAuth) {
          setError('Connect your Jira account to load Discovery spaces.');
        } else {
          setError(data.error || 'Failed to load Discovery spaces');
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load Discovery spaces');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const query = search.trim().toLowerCase();
  const options = spaces.filter((sp) => !query
    || sp.key.toLowerCase().includes(query)
    || sp.name.toLowerCase().includes(query));
  const selected = spaces.find((sp) => sp.key === value) || null;

  return (
    <div style={s.spacePicker}>
      <div style={s.spaceLabel}>🧭 Product Discovery space</div>
      <p style={s.help}>
        Pick the Discovery space that holds the Idea (e.g. <strong>PM - Product Management</strong>).
        This is separate from the project selected at the top of the page.
      </p>
      {loading ? (
        <p style={s.help}>⏳ Loading Discovery spaces…</p>
      ) : error ? (
        <div style={s.error}>{error}</div>
      ) : spaces.length === 0 ? (
        <p style={s.help}>No Jira Product Discovery spaces found on your account.</p>
      ) : (
        <>
          {spaces.length > 6 && (
            <input
              style={{ ...s.input, marginBottom: 8 }}
              placeholder="🔍 Filter Discovery spaces…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
          <select
            style={s.input}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          >
            <option value="">— Select a Discovery space ({options.length}) —</option>
            {options.map((sp) => (
              <option key={sp.key} value={sp.key}>{sp.name} ({sp.key})</option>
            ))}
          </select>
          {selected && (
            <div style={s.help}>Ideas will be searched in <strong>{selected.name}</strong> ({selected.key}).</div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Epic / Story). The tickets themselves are created in the referenced tab; here
 * we link existing tickets into the flow so downstream documents can reference
 * them. Mirrors the project-scoped picker used elsewhere (no raw text entry).
 */
function JiraLinkStep({ step, flow, selectedProject, onSaveLinks, emptyHint }) {
  const bucket = step.jiraBucket;
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [linked, setLinked] = useState(flow.linkedTickets?.[bucket] || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setLinked(flow.linkedTickets?.[bucket] || []); setSaved(false); }, [flow.id, flow.linkedTickets, bucket]);

  useEffect(() => {
    if (!selectedProject) { setIssues([]); return undefined; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/search-issues?projectKey=${encodeURIComponent(selectedProject)}&query=`);
        const data = await res.json();
        if (!cancelled && res.ok && data.success) setIssues(data.issues || []);
      } catch {
        /* non-fatal */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedProject]);

  const query = search.trim().toLowerCase();
  const typeMatch = (issue) => {
    if (!step.jiraIssueType) return true;
    const t = (issue.fields?.issuetype?.name || '').toLowerCase();
    return t === step.jiraIssueType.toLowerCase();
  };
  const options = issues.filter((i) => i?.key && i?.fields
    && typeMatch(i)
    && !linked.includes(i.key)
    && (!query || i.key.toLowerCase().includes(query) || (i.fields.summary || '').toLowerCase().includes(query)));

  const addKey = (key) => { if (key && !linked.includes(key)) { setLinked((p) => [...p, key]); setSaved(false); } };
  const removeKey = (key) => { setLinked((p) => p.filter((k) => k !== key)); setSaved(false); };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSaveLinks(bucket, linked);
      setSaved(true);
    } catch (err) {
      setError(err.message || 'Failed to save links');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={s.refNote}>
        Create <strong>{step.name}</strong> tickets in the <strong>{step.tool}</strong> tab, then link them here so
        downstream documents can reference them.
      </div>

      {!selectedProject ? (
        <p style={s.help}>{emptyHint || 'Select a project at the top to search its tickets.'}</p>
      ) : (
        <>
          <input
            style={s.input}
            placeholder={`🔍 Search ${step.jiraIssueType || 'tickets'} by key or summary…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={loading}
          />
          <select
            style={{ ...s.input, marginTop: 8 }}
            value=""
            onChange={(e) => { addKey(e.target.value); setSearch(''); }}
            disabled={loading || issues.length === 0}
          >
            <option value="">
              {loading ? '⏳ Loading tickets…' : `— Link a ${step.jiraIssueType || 'ticket'} (${options.length}) —`}
            </option>
            {options.map((i) => (
              <option key={i.key} value={i.key}>{i.key} — {i.fields.summary}</option>
            ))}
          </select>

          {linked.length > 0 && (
            <div style={s.chipWrap}>
              {linked.map((k) => (
                <span key={k} style={s.chip}>
                  <JiraTicketLink ticketKey={k} />
                  <button style={s.chipX} onClick={() => removeKey(k)} title="Unlink">×</button>
                </span>
              ))}
            </div>
          )}

          {error && <div style={s.error}>{error}</div>}
          <div style={s.saveRow}>
            <button style={{ ...s.saveBtn, ...(saving ? s.btnDisabled : {}) }} onClick={save} disabled={saving}>
              {saving ? 'Saving…' : '💾 Save linked tickets'}
            </button>
            {saved && <span style={s.savedNote}>✓ Saved</span>}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * The SDLC deliverable-flow tab. Guides a user through the ordered deliverable
 * chain from the PM's spec: start/resume a flow, link Jira tickets, generate and
 * edit the SRS/VEP/Test-Case/SDS documents, and track progress per flow.
 */
const SdlcFlow = ({ selectedProject, selectedModel, userInfo }) => {
  const [viewMode, setViewMode] = useState('flow'); // 'flow' | 'quick'
  const [definition, setDefinition] = useState([]);
  const [flows, setFlows] = useState([]);
  const [flow, setFlow] = useState(null);
  const [activeStepKey, setActiveStepKey] = useState('');
  const [showCreateEpic, setShowCreateEpic] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(false);

  const stepsByKey = definition.reduce((acc, s) => { acc[s.key] = s; return acc; }, {});

  // Load the static flow definition once.
  useEffect(() => {
    (async () => {
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/qa/flow-definition`);
        const data = await res.json();
        if (data.success) setDefinition(data.steps || []);
      } catch {
        /* non-fatal */
      }
    })();
  }, []);

  const loadFlows = useCallback(async () => {
    setLoadingFlows(true);
    setError(null);
    try {
      const qs = selectedProject ? `?projectKey=${encodeURIComponent(selectedProject)}` : '';
      const res = await authenticatedFetch(`${apiUrl}/api/qa/flows${qs}`);
      const data = await res.json();
      if (res.ok && data.success) setFlows(data.flows || []);
      else setError(data.error || 'Failed to load flows');
    } catch (err) {
      setError(err.message || 'Failed to load flows');
    } finally {
      setLoadingFlows(false);
    }
  }, [selectedProject]);

  useEffect(() => { loadFlows(); }, [loadFlows]);

  // Collapse the Create-Epic panel whenever the active step or flow changes.
  useEffect(() => { setShowCreateEpic(false); }, [activeStepKey, flow?.id]);

  // Keep the selected flow in sync with the reloaded list.
  useEffect(() => {
    if (!flow) return;
    const fresh = flows.find((f) => f.id === flow.id);
    if (fresh) setFlow(fresh);
  }, [flows]); // eslint-disable-line react-hooks/exhaustive-deps

  const createFlow = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/qa/flows`, {
        method: 'POST',
        body: JSON.stringify({ name, jiraProjectKey: selectedProject || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to create flow');
      setFlows((prev) => [data.flow, ...prev]);
      setFlow(data.flow);
      setActiveStepKey(definition[0]?.key || '');
      setNewName('');
    } catch (err) {
      setError(err.message || 'Failed to create flow');
    } finally {
      setCreating(false);
    }
  };

  const selectFlow = (id) => {
    const f = flows.find((x) => x.id === id) || null;
    setFlow(f);
    setActiveStepKey(f ? (definition[0]?.key || '') : '');
  };

  const patchFlow = async (patch) => {
    if (!flow) return;
    const res = await authenticatedFetch(`${apiUrl}/api/qa/flows/${flow.id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || 'Update failed');
    setFlow(data.flow);
    setFlows((prev) => prev.map((f) => (f.id === data.flow.id ? data.flow : f)));
    return data.flow;
  };

  const toggleStatus = async () => {
    if (!flow) return;
    const next = flow.status === 'active' ? 'stopped' : 'active';
    try { await patchFlow({ status: next }); } catch (err) { setError(err.message); }
  };

  const deleteFlow = () => {
    if (!flow) return;
    setConfirmDelete(true);
  };

  const doDeleteFlow = async () => {
    setConfirmDelete(false);
    if (!flow) return;
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/qa/flows/${flow.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Delete failed');
      setFlows((prev) => prev.filter((f) => f.id !== flow.id));
      setFlow(null);
      setActiveStepKey('');
    } catch (err) {
      setError(err.message);
    }
  };

  const saveLinks = async (bucket, keys) => {
    const linkedTickets = { ...flow.linkedTickets, [bucket]: keys };
    await patchFlow({ linkedTickets });
  };

  // Link an Epic created by the Create-Epic panel into the flow's `epics`
  // bucket, so the step shows complete and downstream steps can reference it.
  const linkCreatedEpic = async (epicKey) => {
    const existing = flow.linkedTickets?.epics || [];
    if (existing.includes(epicKey)) return;
    await saveLinks('epics', [...existing, epicKey]);
  };

  // Link Stories created by the Create-Stories panel into the flow's `stories`
  // bucket, so the Story step shows complete and downstream docs can reference them.
  const linkCreatedStories = async (storyKeys) => {
    const existing = flow.linkedTickets?.stories || [];
    const merged = [...existing, ...storyKeys.filter((k) => !existing.includes(k))];
    if (merged.length === existing.length) return;
    await saveLinks('stories', merged);
  };

  // Persist the draft (not-yet-created) AI-suggested Stories for a parent Epic
  // so they survive navigating away from the Story step and page reloads. Keyed
  // by Epic; the server merges per-Epic so other Epics' drafts are preserved.
  const saveStoryDrafts = async (epicKey, drafts) => {
    if (!flow || !epicKey) return;
    await patchFlow({ storyDrafts: { [epicKey]: Array.isArray(drafts) ? drafts : [] } });
  };

  // Set the Step 1 Product Discovery space. Updates local state optimistically
  // and merges the persisted result so the choice sticks immediately — and is
  // preserved even if the running server build predates jpdProjectKey.
  const setJpdSpace = async (key) => {
    if (!flow) return;
    const value = key || null;
    const flowId = flow.id;
    setFlow((f) => (f && f.id === flowId ? { ...f, jpdProjectKey: value } : f));
    setFlows((prev) => prev.map((f) => (f.id === flowId ? { ...f, jpdProjectKey: value } : f)));
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/qa/flows/${flowId}`, {
        method: 'PATCH',
        body: JSON.stringify({ jpdProjectKey: value }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Update failed');
      const merged = { ...data.flow, jpdProjectKey: data.flow.jpdProjectKey ?? value };
      setFlow((f) => (f && f.id === flowId ? merged : f));
      setFlows((prev) => prev.map((f) => (f.id === flowId ? merged : f)));
    } catch (err) {
      setError(err.message);
    }
  };

  const saveDeliverable = async (docType, payload) => {
    const res = await authenticatedFetch(`${apiUrl}/api/qa/flows/${flow.id}/deliverables/${encodeURIComponent(docType)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
    setFlow(data.flow);
    setFlows((prev) => prev.map((f) => (f.id === data.flow.id ? data.flow : f)));
    return data.flow;
  };

  const activeStep = definition.find((s) => s.key === activeStepKey) || null;
  const doneCount = flow ? definition.filter((s) => stepStatus(flow, s) === 'complete').length : 0;

  if (viewMode === 'quick') {
    return (
      <div style={s.container}>
        <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
        <QaDocForm selectedProject={selectedProject} selectedModel={selectedModel} userInfo={userInfo} />
      </div>
    );
  }

  return (
    <div style={s.container}>
      <LyricConfirm
        isOpen={confirmDelete}
        title="Delete flow?"
        message={flow ? `Delete flow “${flow.name}”? This cannot be undone.` : ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={doDeleteFlow}
        onCancel={() => setConfirmDelete(false)}
      />
      <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />

      <h2 style={s.title}>🧭 SDLC Deliverable Flow</h2>
      <p style={s.subtitle}>
        Walk a feature through the deliverable chain — link Jira tickets, design an interactive mockup, then generate
        and edit the SRS, VEP, Test Cases, and SDS. Progress is saved so you can stop and resume anytime.
      </p>

      {error && <div style={s.error}>{error}</div>}

      {/* Flow bar */}
      <div style={s.flowBar}>
        <select style={s.flowSelect} value={flow?.id || ''} onChange={(e) => selectFlow(e.target.value)}>
          <option value="">
            {loadingFlows ? '⏳ Loading flows…' : (flows.length ? '— Select a flow —' : 'No flows yet — create one →')}
          </option>
          {flows.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}{f.jiraProjectKey ? ` · ${f.jiraProjectKey}` : ''} · {STATUS_LABEL[f.status] || f.status}
            </option>
          ))}
        </select>
        <input
          style={s.newInput}
          placeholder="New flow name (e.g. Login revamp)…"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') createFlow(); }}
        />
        <button style={{ ...s.primaryBtn, ...(creating || !newName.trim() ? s.btnDisabled : {}) }} onClick={createFlow} disabled={creating || !newName.trim()}>
          {creating ? 'Creating…' : '➕ New flow'}
        </button>
      </div>

      {!flow ? (
        <p style={s.help}>Select an existing flow or create a new one to begin. Flows are scoped to the selected project{selectedProject ? ` (${selectedProject})` : ''}.</p>
      ) : (
        <>
          {/* Flow header + controls */}
          <div style={s.flowHeader}>
            <div>
              <div style={s.flowName}>{flow.name}</div>
              <div style={s.flowMeta}>
                {flow.jiraProjectKey ? `${flow.jiraProjectKey} · ` : ''}
                <span style={flow.status === 'active' ? s.statusActive : s.statusStopped}>{STATUS_LABEL[flow.status] || flow.status}</span>
                {' · '}{doneCount}/{definition.length} complete
              </div>
            </div>
            <div style={s.flowControls}>
              <button style={s.secondaryBtn} onClick={toggleStatus}>
                {flow.status === 'active' ? '⏸ Stop' : '▶ Resume'}
              </button>
              <button style={{ ...s.secondaryBtn, color: '#b91c1c' }} onClick={deleteFlow}>🗑 Delete</button>
            </div>
          </div>

          <div style={s.body}>
            {/* Step rail */}
            <div style={s.rail}>
              <div style={s.railHeader}>
                <span style={s.railHeaderLabel}>Workflow steps</span>
                <button
                  style={s.railToggle}
                  onClick={() => setRailCollapsed((v) => !v)}
                  title={railCollapsed ? 'Expand steps' : 'Collapse steps'}
                >
                  {railCollapsed ? '▸ Expand' : '▾ Collapse'}
                </button>
              </div>

              {railCollapsed ? (
                <div style={s.railCompact}>
                  {definition.map((step) => {
                    const status = stepStatus(flow, step);
                    const active = step.key === activeStepKey;
                    return (
                      <button
                        key={step.key}
                        style={{ ...s.railCompactBadge, ...badgeStyle(status), ...(active ? s.railCompactActive : {}) }}
                        onClick={() => setActiveStepKey(step.key)}
                        title={`${step.order}. ${step.name} — ${STATUS_LABEL[status]}`}
                      >
                        {step.order}
                      </button>
                    );
                  })}
                </div>
              ) : (
                definition.map((step) => {
                  const status = stepStatus(flow, step);
                  const unlocked = isUnlocked(flow, step, stepsByKey);
                  const active = step.key === activeStepKey;
                  return (
                    <button
                      key={step.key}
                      style={{ ...s.railItem, ...(active ? s.railItemActive : {}) }}
                      onClick={() => setActiveStepKey(step.key)}
                    >
                      <span style={{ ...s.railBadge, ...badgeStyle(status) }}>{step.order}</span>
                      <span style={s.railText}>
                        <span style={s.railName}>{step.name}</span>
                        <span style={s.railStatus}>
                          {step.kind === 'jira' ? 'Jira · ' : step.kind === 'design' ? 'Design · ' : 'Document · '}
                          {STATUS_LABEL[status]}
                          {!unlocked ? ' · 🔒 waiting on prerequisites' : ''}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            {/* Active step panel */}
            <div style={s.panel}>
              {!activeStep ? (
                <p style={s.help}>Select a deliverable from the list to work on it.</p>
              ) : (
                <>
                  {!isUnlocked(flow, activeStep, stepsByKey) && (
                    <div style={s.warn}>
                      ⚠ This deliverable normally depends on: {activeStep.requires.map((k) => stepsByKey[k]?.name || k).join(', ')}.
                      You can still work on it, but completing the prerequisites first is recommended.
                    </div>
                  )}

                  {activeStep.kind === 'jira' ? (
                    <div style={s.jiraPanel}>
                      <h3 style={s.stepTitle}>{activeStep.order}. {activeStep.name}</h3>
                      <p style={s.help}><strong>Output:</strong> {activeStep.output}</p>
                      {activeStep.key === 'JPD' && (
                        <JpdSpacePicker
                          value={flow.jpdProjectKey || ''}
                          onChange={setJpdSpace}
                        />
                      )}
                      {activeStep.key !== 'STORY' && (
                        <JiraLinkStep
                          step={activeStep}
                          flow={flow}
                          selectedProject={activeStep.key === 'JPD'
                            ? (flow.jpdProjectKey || selectedProject)
                            : selectedProject}
                          emptyHint={activeStep.key === 'JPD'
                            ? 'Select a Product Discovery space above to search its ideas.'
                            : undefined}
                          onSaveLinks={saveLinks}
                        />
                      )}
                      {activeStep.key === 'EPIC' && (
                        <div style={s.createEpicWrap}>
                          <button
                            style={{ ...s.primaryBtn, ...(showCreateEpic ? s.secondaryBtn : {}) }}
                            onClick={() => setShowCreateEpic((v) => !v)}
                          >
                            {showCreateEpic ? '✕ Close Create Epic' : '➕ Create EPIC'}
                          </button>
                          {showCreateEpic && (
                            <p style={s.help}>The Create Epic form is shown below, using the full width.</p>
                          )}
                        </div>
                      )}
                      {activeStep.key === 'STORY' && (
                        <p style={s.help}>The Story builder is shown below, using the full width.</p>
                      )}
                    </div>
                  ) : activeStep.kind === 'design' ? (
                    <div style={s.jiraPanel}>
                      <h3 style={s.stepTitle}>{activeStep.order}. {activeStep.name}</h3>
                      <p style={s.help}><strong>Output:</strong> {activeStep.output}</p>
                      <p style={s.help}>The mockup builder is shown below, using the full width.</p>
                    </div>
                  ) : (
                    <QaDocForm
                      key={`${flow.id}:${activeStep.key}`}
                      selectedProject={selectedProject}
                      selectedModel={selectedModel}
                      userInfo={userInfo}
                      lockedDocType={activeStep.docType}
                      flowStep={activeStep}
                      initialDocument={flow.deliverables?.[activeStep.docType]?.markdown
                        ? flow.deliverables[activeStep.docType]
                        : null}
                      onSaveDeliverable={saveDeliverable}
                      flowName={flow.name}
                      defaultJiraKeys={activeStep.key === 'SRS'
                        ? (flow.linkedTickets?.epics || []).slice(0, 1)
                        : []}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {/* Create Epic form — rendered full width below the rail/panel columns
              so the summary and rich-text description get the whole area. */}
          {activeStep && activeStep.key === 'EPIC' && showCreateEpic && (
            <div style={s.fullWidthPanel}>
              <h3 style={s.stepTitle}>➕ Create Epic</h3>
              <CreateEpicPanel
                flow={flow}
                selectedProject={selectedProject}
                selectedModel={selectedModel}
                userInfo={userInfo}
                onEpicCreated={linkCreatedEpic}
              />
            </div>
          )}

          {/* Create Stories — rendered full width below the rail/panel columns
              so the story cards and rich-text editors get the whole area. */}
          {activeStep && activeStep.key === 'STORY' && (
            <div style={s.fullWidthPanel}>
              <h3 style={s.stepTitle}>➕ Create Stories</h3>
              <CreateStoriesPanel
                flow={flow}
                selectedProject={selectedProject}
                selectedModel={selectedModel}
                onStoriesCreated={linkCreatedStories}
                onSaveDrafts={saveStoryDrafts}
              />
            </div>
          )}

          {/* Design mockup builder — rendered full width below the rail/panel
              columns so the linked-stories picker and live editor get the whole
              area (matching the EPIC/STORY steps). */}
          {activeStep && activeStep.kind === 'design' && (
            <div style={s.fullWidthPanel}>
              <MockupBuilder
                key={`${flow.id}:${activeStep.key}`}
                flow={flow}
                step={activeStep}
                selectedProject={selectedProject}
                selectedModel={selectedModel}
                onSave={saveDeliverable}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

function ViewToggle({ viewMode, setViewMode }) {
  return (
    <div style={s.viewToggle}>
      <button style={{ ...s.viewBtn, ...(viewMode === 'flow' ? s.viewBtnActive : {}) }} onClick={() => setViewMode('flow')}>
        🧭 Guided flow
      </button>
      <button style={{ ...s.viewBtn, ...(viewMode === 'quick' ? s.viewBtnActive : {}) }} onClick={() => setViewMode('quick')}>
        📄 Quick document (no flow)
      </button>
    </div>
  );
}

function badgeStyle(status) {
  if (status === 'complete') return { background: '#047857', color: '#fff', borderColor: '#047857' };
  if (status === 'in_progress') return { background: '#b45309', color: '#fff', borderColor: '#b45309' };
  return { background: '#fff', color: '#666', borderColor: '#ccc' };
}

const s = {
  container: { maxWidth: 1040, margin: '0 auto', padding: '8px 4px', fontFamily: 'inherit' },
  viewToggle: { display: 'flex', gap: 8, marginBottom: 16 },
  viewBtn: { padding: '8px 14px', border: '1px solid #ccc', borderRadius: 8, background: '#fff', color: '#333', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  viewBtnActive: { background: '#1a56db', color: '#fff', borderColor: '#1a56db' },
  title: { fontSize: 22, margin: '0 0 4px', color: '#222' },
  subtitle: { color: '#555', marginTop: 0, marginBottom: 16, fontSize: 14 },
  flowBar: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 },
  flowSelect: { flex: '1 1 260px', padding: '9px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 },
  newInput: { flex: '1 1 200px', padding: '9px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 },
  primaryBtn: { background: '#1a56db', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  secondaryBtn: { background: '#fff', border: '1px solid #ccc', padding: '7px 12px', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#333' },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  flowHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: '#f3f4f6', border: '1px solid #e3e3e3', borderRadius: 8, marginBottom: 14 },
  flowName: { fontWeight: 700, fontSize: 16, color: '#222' },
  flowMeta: { fontSize: 13, color: '#555', marginTop: 2 },
  statusActive: { color: '#047857', fontWeight: 600 },
  statusStopped: { color: '#b45309', fontWeight: 600 },
  flowControls: { display: 'flex', gap: 8 },
  body: { display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' },
  rail: { flex: '1 1 300px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 8 },
  railHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 2 },
  railHeaderLabel: { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: '#888' },
  railToggle: { background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 2 },
  railCompact: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  railCompactBadge: { width: 30, height: 30, borderRadius: '50%', border: '1px solid #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  railCompactActive: { boxShadow: '0 0 0 2px #1a56db', outline: 'none' },
  railItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid #e3e3e3', borderRadius: 8, background: '#fafafa', cursor: 'pointer', textAlign: 'left', width: '100%' },
  railItemActive: { borderColor: '#1a56db', background: '#f5f8ff', boxShadow: '0 0 0 1px #1a56db inset' },
  railBadge: { width: 26, height: 26, flexShrink: 0, borderRadius: '50%', border: '1px solid #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 },
  railText: { display: 'flex', flexDirection: 'column' },
  railName: { fontSize: 14, fontWeight: 600, color: '#222' },
  railStatus: { fontSize: 12, color: '#777' },
  panel: { flex: '2 1 480px', minWidth: 320, border: '1px solid #e3e3e3', borderRadius: 8, padding: 14, background: '#fff' },
  stepTitle: { fontSize: 18, margin: '0 0 6px', color: '#222' },
  jiraPanel: {},
  createEpicWrap: { marginTop: 16, paddingTop: 14, borderTop: '1px solid #eee' },
  fullWidthPanel: { width: '100%', marginTop: 16, border: '1px solid #e3e3e3', borderRadius: 8, padding: 14, background: '#fff', boxSizing: 'border-box' },
  refNote: { background: '#f5f8ff', border: '1px solid #dbe4ff', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#333', marginBottom: 12 },
  spacePicker: { background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 8, padding: '12px 14px', marginBottom: 14 },
  spaceLabel: { fontSize: 14, fontWeight: 700, color: '#6b21a8' },
  warn: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 12 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  help: { color: '#777', fontSize: 12, margin: '6px 0 0' },
  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#e8f0fe', color: '#1a56db', padding: '4px 8px', borderRadius: 12, fontSize: 13 },
  chipX: { background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 0 },
  error: { background: '#fde8e8', color: '#9b1c1c', padding: '10px 12px', borderRadius: 6, margin: '10px 0', fontSize: 14 },
  saveRow: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 },
  saveBtn: { background: '#047857', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  savedNote: { color: '#047857', fontSize: 13, fontWeight: 600 },
};

export default SdlcFlow;
